import { useEffect } from 'react'
import { Button, Stack, Typography } from '@mui/material'
import { Controller, Form, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAlert } from '~/@ui/alert/useAlert'
import { Link, useNavigate } from 'react-router-dom'
import { useLoggedInUser } from '~/@biz_account/useLoggedInUser/useLoggedInUser'
import { usePhoneStore } from './phoneStore'
import {
  PhoneScheme,
  phoneScheme,
} from '~/@biz_account/phone-login/_scheme/phone.scheme'
import { StyledSelect } from '~/@ui/form/StyledSelect'
import { StyledMenuItem } from '~/@ui/form/StyledMenuItem'
import { FormStyledTextField } from '~/@ui/form/FormStyledTextField'
import { getPhoneCountries } from '~/@utils/phone/getPhoneCountries'
import { Header } from '~/@ui/header/Header'

export const ChangePhonePage = () => {
  const { showAlert } = useAlert()
  const { checkLoggedInUser } = useLoggedInUser()
  const { setPhone } = usePhoneStore()
  const navigate = useNavigate()

  const { countries, getCountryByCode } = getPhoneCountries()

  const isInvestorRoute = location.pathname.includes('/investor/')

  const { control, getValues, setValue, trigger } = useForm<PhoneScheme>({
    resolver: zodResolver(phoneScheme),
    mode: 'onBlur',
    defaultValues: {
      phonePrefix: '+421',
      phoneNumber: '',
    },
  })

  const getPhoneNumber = async () => {
    const response = await checkLoggedInUser()

    const phoneNumber = response?.user?.phoneNumber
    if (phoneNumber) {
      const match = phoneNumber.match(/^(\+\d{0,3})(\d+)$/)

      if (match) {
        setValue('phonePrefix', match[1])
        setValue('phoneNumber', match[2])
      }
    }
  }

  const editNumberAndVerify = async () => {
    const isValid = await trigger()

    if (!isValid) {
      showAlert('error', 'Prosím, vyplňte tel. číslo správne.')
      return
    }

    const phone = `${getValues('phonePrefix')} ${getValues('phoneNumber')}`

    setPhone(phone)

    navigate(
      isInvestorRoute
        ? '/investor/user-profile/change-phone/code'
        : '/vlastnik/user-profile/change-phone/code'
    )
  }

  useEffect(() => {
    getPhoneNumber()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Stack className="w-full h-full">
      <Stack className="w-full max-w-[768px] self-center px-6 py-4">
        <Button
          component={Link}
          to={
            isInvestorRoute
              ? '/investor/user-profile'
              : '/vlastnik/user-profile'
          }
          className="w-16 h-11 bg-zGrey200">
          <Typography className="text-xl font-medium">{'<-'}</Typography>
        </Button>
      </Stack>

      <Stack className="w-full max-w-[768px] self-center px-5">
        <Header
          title="Zmena tel. čísla"
          text="Nastavte svoje nové číslo, ktoré budete používať na prihlasovanie."
          className="pt-6"
        />

        <Form control={control} className="flex flex-row w-full gap-1d mt-8">
          <Controller
            name="phonePrefix"
            control={control}
            defaultValue="+421"
            render={({ field }) => (
              <StyledSelect
                {...field}
                renderValue={(value: unknown): React.ReactNode => {
                  const selectedCountry = getCountryByCode(value as string)

                  if (!selectedCountry) return value as string

                  return (
                    <Stack className="flex-row items-center gap-2">
                      {selectedCountry.flag}
                      <Typography className="text-3d font-semibold text-zBlack200 tabular-nums">
                        {selectedCountry.code}
                      </Typography>
                    </Stack>
                  )
                }}>
                {countries.map(({ code, flag, name }) => (
                  <StyledMenuItem key={code} value={code}>
                    {flag}
                    <Typography className="text-3d font-semibold text-zBlack200 tabular-nums">
                      {code}
                    </Typography>
                    <Typography className="text-3d font-medium text-zGrey400">
                      {name}
                    </Typography>
                  </StyledMenuItem>
                ))}
              </StyledSelect>
            )}
          />

          <FormStyledTextField
            control={control}
            name="phoneNumber"
            formatNumber
            placeholder="Telefónne číslo"
            inputMode="numeric"
            type="tel"
            slotProps={{
              htmlInput: {
                pattern: '[0-9]*',
                maxLength: 15,
              },
            }}
          />
        </Form>

        <Button
          onClick={editNumberAndVerify}
          variant="contained"
          className="h-[56px] mt-6">
          <Typography className="text-3d font-medium text-zWhite200">
            Pokračovať
          </Typography>
        </Button>
      </Stack>
    </Stack>
  )
}
